import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Pagination,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { Search, Message, MessageOutlined, Inbox } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useGetTwilioConfigurationsQuery, useGetTwilioMessagesQuery } from '../store/api/twilioApi';

export const TwilioSMSPage: React.FC = () => {
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [directionFilter, setDirectionFilter] = useState<string>('all');
  
  const { token, isAuthenticated } = useSelector((state: any) => state.auth);
  const pageSize = 25;

  // Get configurations
  const { data: configurations, isLoading: configsLoading } = useGetTwilioConfigurationsQuery();
  
  // Get messages data
  const { 
    data: messages, 
    isLoading: messagesLoading, 
    error: messagesError 
  } = useGetTwilioMessagesQuery(
    { 
      configId: selectedConfigId!, 
      limit: pageSize, 
      offset: (page - 1) * pageSize 
    },
    { skip: !selectedConfigId }
  );

  // Set first configuration as default
  useEffect(() => {
    if (configurations && configurations.length > 0 && !selectedConfigId) {
      setSelectedConfigId(configurations[0].id);
    }
  }, [configurations, selectedConfigId]);

  const getDirectionIcon = (direction: string) => {
    switch (direction?.toLowerCase()) {
      case 'outbound-api':
      case 'outbound':
        return <MessageOutlined color="primary" />;
      case 'inbound':
        return <Inbox color="success" />;
      default:
        return <Message />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
      case 'sent':
        return 'success';
      case 'queued':
      case 'sending':
        return 'warning';
      case 'failed':
      case 'undelivered':
        return 'error';
      default:
        return 'default';
    }
  };

  const getDirectionColor = (direction: string) => {
    switch (direction?.toLowerCase()) {
      case 'outbound-api':
      case 'outbound':
        return 'primary';
      case 'inbound':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const filteredMessages = messages?.filter(message => {
    const matchesSearch = !searchTerm || 
      message.from_number?.includes(searchTerm) || 
      message.to_number?.includes(searchTerm) ||
      message.body?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.sid?.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || message.status === statusFilter;
    const matchesDirection = directionFilter === 'all' || message.direction === directionFilter;
    
    return matchesSearch && matchesStatus && matchesDirection;
  }) || [];

  // Calculate totals for different statuses and directions
  const statusTotals = React.useMemo(() => {
    if (!messages) {
      return {
        all: 0,
        delivered: 0,
        sent: 0,
        queued: 0,
        sending: 0,
        failed: 0,
        undelivered: 0,
        outbound: 0,
        inbound: 0
      };
    }
    
    const totals = {
      all: messages.length,
      delivered: 0,
      sent: 0,
      queued: 0,
      sending: 0,
      failed: 0,
      undelivered: 0,
      outbound: 0,
      inbound: 0
    };
    
    messages.forEach(message => {
      const status = message.status?.toLowerCase();
      const direction = message.direction?.toLowerCase();
      
      if (status === 'delivered') totals.delivered++;
      else if (status === 'sent') totals.sent++;
      else if (status === 'queued') totals.queued++;
      else if (status === 'sending') totals.sending++;
      else if (status === 'failed') totals.failed++;
      else if (status === 'undelivered') totals.undelivered++;
      
      if (direction === 'outbound-api' || direction === 'outbound') totals.outbound++;
      else if (direction === 'inbound') totals.inbound++;
    });
    
    return totals;
  }, [messages]);

  if (!isAuthenticated) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error">Please log in to view Twilio SMS messages.</Alert>
      </Box>
    );
  }

  if (configsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!configurations || configurations.length === 0) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Twilio SMS Messages
        </Typography>
        <Alert severity="info">
          No Twilio configurations found. Please create a configuration first.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Twilio SMS Messages
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        View and manage your Twilio SMS message logs
      </Typography>

      {/* Configuration Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Configuration</InputLabel>
                <Select
                  value={selectedConfigId || ''}
                  onChange={(e) => setSelectedConfigId(Number(e.target.value))}
                  label="Configuration"
                >
                  {configurations.map((config) => (
                    <MenuItem key={config.id} value={config.id}>
                      {config.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                placeholder="Search messages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={2.5}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="delivered">Delivered</MenuItem>
                  <MenuItem value="sent">Sent</MenuItem>
                  <MenuItem value="queued">Queued</MenuItem>
                  <MenuItem value="sending">Sending</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                  <MenuItem value="undelivered">Undelivered</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2.5}>
              <FormControl fullWidth>
                <InputLabel>Direction</InputLabel>
                <Select
                  value={directionFilter}
                  onChange={(e) => setDirectionFilter(e.target.value)}
                  label="Direction"
                >
                  <MenuItem value="all">All Directions</MenuItem>
                  <MenuItem value="outbound-api">Outbound</MenuItem>
                  <MenuItem value="inbound">Inbound</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Status Totals */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            SMS Statistics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={1.5}>
              <Box textAlign="center" p={2} bgcolor="primary.light" borderRadius={1}>
                <Typography variant="h4" color="primary.contrastText">
                  {statusTotals.all || 0}
                </Typography>
                <Typography variant="body2" color="primary.contrastText">
                  Total SMS
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <Box textAlign="center" p={2} bgcolor="success.light" borderRadius={1}>
                <Typography variant="h4" color="success.contrastText">
                  {statusTotals.delivered || 0}
                </Typography>
                <Typography variant="body2" color="success.contrastText">
                  Delivered
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <Box textAlign="center" p={2} bgcolor="info.light" borderRadius={1}>
                <Typography variant="h4" color="info.contrastText">
                  {statusTotals.sent || 0}
                </Typography>
                <Typography variant="body2" color="info.contrastText">
                  Sent
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <Box textAlign="center" p={2} bgcolor="warning.light" borderRadius={1}>
                <Typography variant="h4" color="warning.contrastText">
                  {statusTotals.queued || 0}
                </Typography>
                <Typography variant="body2" color="warning.contrastText">
                  Queued
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <Box textAlign="center" p={2} bgcolor="error.light" borderRadius={1}>
                <Typography variant="h4" color="error.contrastText">
                  {statusTotals.failed || 0}
                </Typography>
                <Typography variant="body2" color="error.contrastText">
                  Failed
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <Box textAlign="center" p={2} bgcolor="secondary.light" borderRadius={1}>
                <Typography variant="h4" color="secondary.contrastText">
                  {statusTotals.outbound || 0}
                </Typography>
                <Typography variant="body2" color="secondary.contrastText">
                  Outbound
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <Box textAlign="center" p={2} bgcolor="grey.200" borderRadius={1}>
                <Typography variant="h4" color="text.primary">
                  {filteredMessages.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Filtered Results
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Messages Table */}
      <Card>
        <CardContent>
          {messagesLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : messagesError ? (
            <Alert severity="error">
              Error loading messages: {messagesError.toString()}
            </Alert>
          ) : filteredMessages.length === 0 ? (
            <Alert severity="info">
              No messages found for the selected configuration.
            </Alert>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Direction</TableCell>
                      <TableCell>From</TableCell>
                      <TableCell>To</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Segments</TableCell>
                      <TableCell>Date Sent</TableCell>
                      <TableCell>Price</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredMessages.map((message) => (
                      <TableRow key={message.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {getDirectionIcon(message.direction || '')}
                            <Chip
                              label={message.direction || 'Unknown'}
                              color={getDirectionColor(message.direction || '') as any}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>{message.from_number || 'N/A'}</TableCell>
                        <TableCell>{message.to_number || 'N/A'}</TableCell>
                        <TableCell>
                          <Tooltip title={message.body || 'N/A'} placement="top">
                            <Typography variant="body2" sx={{ maxWidth: 200 }}>
                              {truncateText(message.body || 'N/A')}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={message.status || 'Unknown'}
                            color={getStatusColor(message.status || '') as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{message.num_segments || 'N/A'}</TableCell>
                        <TableCell>{message.date_sent ? formatDate(message.date_sent) : 'N/A'}</TableCell>
                        <TableCell>
                          {message.price ? `${message.price} ${message.price_unit || ''}` : 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={Math.ceil((messages?.length || 0) / pageSize)}
                  page={page}
                  onChange={(_, newPage) => setPage(newPage)}
                  color="primary"
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
